/**
 * PendingConfirmationsCard — Card exibido no topo da Agenda.
 *
 * Lista agendamentos scheduled para hoje/amanhã que ainda não foram
 * confirmados pelo cliente. Cada linha traz um botão "Enviar confirmação"
 * que abre o WhatsApp com a mensagem pré-composta contendo hora + serviço.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, Loader2, CalendarCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";

const fmtHM = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};
const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
};

export default function PendingConfirmationsCard({ tenantName }) {
  const [data, setData] = useState({ items: [], count_today: 0, count_tomorrow: 0 });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/tenant/agenda/pending-confirmations", { params: { hours: 48 } });
      setData(d);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || (data.items || []).length === 0) return null;

  const send = async (a) => {
    if (!a.client_phone) {
      toast.warning("Cliente sem WhatsApp cadastrado");
      return;
    }
    setSending(a.id);
    const firstName = (a.client_name || "").split(" ")[0] || "";
    const dayLabel = a._bucket === "hoje" ? "hoje" : `amanhã (${fmtDay(a.start_at)})`;
    const msg =
      `Olá ${firstName}! 👋\n\n` +
      `Passando para confirmar seu horário ${dayLabel} às ${fmtHM(a.start_at)} — ${a.service_name}` +
      (a.resource_name ? ` com ${a.resource_name}` : "") + `.\n\n` +
      `Podemos confirmar? Responda SIM para reservar. 😊\n\n` +
      `${tenantName || ""}`;
    try {
      await sendWhatsApp({ phone: a.client_phone, text: msg, context: "confirmation", clientId: a.client_id });
      toast.success(`Confirmação enviada para ${firstName}`);
    } catch { toast.error("Não foi possível enviar."); }
    finally { setSending(null); }
  };

  return (
    <Card className="rounded-tenant border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="pending-confirmations-card">
      <div className="flex items-center gap-2">
        <CalendarCheck className="w-4 h-4 text-amber-700" />
        <div className="text-xs uppercase tracking-wide text-amber-800 font-semibold">
          Confirmar próximos agendamentos
        </div>
        <div className="text-[10px] text-muted-foreground ml-auto">
          Hoje: {data.count_today} · Amanhã: {data.count_tomorrow}
        </div>
      </div>

      <ul className="divide-y divide-amber-500/10">
        {data.items.map((a) => (
          <li key={a.id} className="py-2 flex items-center gap-2 flex-wrap"
            data-testid={`pending-confirm-${a.id}`}>
            <Badge variant="outline" className="text-[10px] shrink-0 bg-amber-500/10 text-amber-700 border-amber-500/40">
              {a._bucket === "hoje" ? "Hoje" : "Amanhã"}
            </Badge>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{a.client_name}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {fmtHM(a.start_at)} · {a.service_name}
                {(() => {
                  const names = (a.resource_names && a.resource_names.length > 0)
                    ? a.resource_names
                    : (a.resource_name ? [a.resource_name] : []);
                  return names.length > 0 ? ` · ${names.join(" + ")}` : "";
                })()}
              </div>
            </div>
            <Button
              size="sm" variant="outline"
              className="h-7 text-xs gap-1 shrink-0"
              disabled={sending === a.id || !a.client_phone}
              onClick={() => send(a)}
              data-testid={`pending-confirm-send-${a.id}`}
            >
              {sending === a.id
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <MessageCircle className="w-3 h-3" />}
              Enviar confirmação
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
